import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Download, Edit, Save, X, FileText, Clock, AlertCircle, CheckCircle, Trash2, Maximize2, Shield, Brain, Sparkles, Share2, Upload, ScanLine, GitBranch, ChevronDown, Activity, Play, Users, XCircle, RotateCcw, RefreshCw, Loader2 } from 'lucide-react';
import api from '../api/api';
import { toast } from 'sonner';
import PDFViewer from '../components/PDFViewer';
import ShareDialog from '../components/ShareDialog';
import { RedactionPanel, AgentInsightsPanel } from '../components/agentic';
import { DocumentAIInsights } from '../components/documentAI';

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [document, setDocument] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [fsZoom, setFsZoom] = useState(1.0);
  const [fsPage, setFsPage] = useState({ current: 1, total: 0 });
  const [versions, setVersions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [startingWf, setStartingWf] = useState(false);
  const [wfActionLoading, setWfActionLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [metadata, setMetadata] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMimeType, setPreviewMimeType] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [convertedFormat, setConvertedFormat] = useState(null);
  const [previewable, setPreviewable] = useState(true);

  const [fullViewError, setFullViewError] = useState(null);
  const [aiInsight, setAiInsight] = useState(null);
  const [indexing, setIndexing] = useState(false);
  const [numericValidation, setNumericValidation] = useState(null);
  const [lineage, setLineage] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);

  useEffect(() => {
    loadDocumentDetails();
  }, [id]);

  // Update summary when document loads with __ai_insight
  useEffect(() => {
    const insight = document?.__ai_insight;
    if (insight) {
      setAiInsight(insight);
      if (insight.summary) {
        setAiSummary(insight.summary);
      }
    }
  }, [document]);

  // Load deterministic numeric validation once AI extraction is complete
  useEffect(() => {
    const insight = document?.__ai_insight || document?.metadata?.__ai_insight;
    if (!insight || insight.status !== 'completed') return;
    api.getNumericValidation(id, getAuthHeaders())
      .then((r) => setNumericValidation(r.data?.validation || null))
      .catch(() => {});
    // eslint-disable-next-line
  }, [document]);

  // Load full data lineage (P3-B)
  useEffect(() => {
    if (!document) return;
    api.getDocumentLineage(id, getAuthHeaders())
      .then((r) => setLineage(r.data?.chain || []))
      .catch(() => {});
    // eslint-disable-next-line
  }, [document]);

  // Keyboard shortcuts for fullscreen viewer
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setFullscreen(false); return; }
      if (e.key === '+' || e.key === '=') { setFsZoom((z) => Math.min(z + 0.25, 3)); e.preventDefault(); return; }
      if (e.key === '-') { setFsZoom((z) => Math.max(z - 0.25, 0.5)); e.preventDefault(); return; }
      if (e.key === 'ArrowLeft') { window.dispatchEvent(new CustomEvent('pdfviewer:nav', { detail: { direction: 'prev' } })); return; }
      if (e.key === 'ArrowRight') { window.dispatchEvent(new CustomEvent('pdfviewer:nav', { detail: { direction: 'next' } })); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const loadDocumentDetails = async () => {
    try {
      const headers = getAuthHeaders();
      
      // Load document
      const docResponse = await api.getDocument(id, headers);
      setDocument(docResponse.data);
      setMetadata({
        title: docResponse.data.title,
        description: docResponse.data.description,
        doc_type: docResponse.data.doc_type,
        tags: docResponse.data.tags
      });

      // Load versions
      try {
        const versionsResponse = await api.getDocumentVersions(id, headers);
        setVersions(versionsResponse.data);
      } catch (err) {
        console.log('No versions endpoint yet');
      }

      // Load audit logs
      try {
        const auditResponse = await api.getAuditLogs({ resource_id: id }, headers);
        setAuditLogs(auditResponse.data.filter(log => log.resource_id === id));
      } catch (err) {
        console.log('Audit logs not available');
      }

      // Load AI insight from the document response directly
      const docData = docResponse.data;
      const insight = docData?.__ai_insight;
      if (insight) {
        setAiInsight(insight);
        if (insight.summary) {
          setAiSummary(insight.summary);
        }
      }

      // Load preview if document has a file
      if (docData.s3_key_current) {
        loadDocumentPreview(docData.id);
      }

    } catch (error) {
      console.error('Error loading document:', error);
      toast.error('Failed to load document details');
    } finally {
      setLoading(false);
    }
  };

  // Load the document's current workflow instance + the list of active workflows.
  const loadWorkflowData = async () => {
    const headers = getAuthHeaders();
    try {
      const r = await api.getWorkflowByDocument(id, headers);
      setWorkflowStatus(r.data?.instance || null);
    } catch (e) { setWorkflowStatus(null); }
    try {
      const w = await api.listWorkflows(headers);
      setWorkflows((w.data || []).filter((x) => x.is_active));
    } catch (e) { /* non-critical */ }
  };

  useEffect(() => {
    if (id) loadWorkflowData();
    // eslint-disable-next-line
  }, [id]);

  const hasActiveWorkflow = workflowStatus &&
    ['In Progress', 'Changes Requested'].includes(workflowStatus.status);

  const handleStartWorkflow = async () => {
    if (!selectedWorkflowId) { toast.error('Please select a workflow'); return; }
    setStartingWf(true);
    try {
      const res = await api.startWorkflow(id, selectedWorkflowId, getAuthHeaders());
      toast.success(res.data?.message || 'Workflow started');
      setStartDialogOpen(false);
      setSelectedWorkflowId('');
      await loadWorkflowData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start workflow');
    } finally {
      setStartingWf(false);
    }
  };

  const handleWorkflowAction = async (action) => {
    if (!workflowStatus?.id) return;
    let comments = '';
    if (action !== 'approve') {
      comments = window.prompt(
        action === 'reject' ? 'Reason for rejection (required):' : 'What changes are required? (required):'
      ) || '';
      if (!comments.trim()) { toast.error('A comment is required for this action'); return; }
    }
    setWfActionLoading(true);
    try {
      await api.performWorkflowAction(workflowStatus.id, action, comments.trim(), getAuthHeaders());
      const verb = action === 'reject' ? 'rejected' : action === 'approve' ? 'approved' : 'returned for changes';
      toast.success(`Workflow ${verb}`);
      await loadWorkflowData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to perform action');
    } finally {
      setWfActionLoading(false);
    }
  };

  const loadDocumentPreview = async (docId) => {
    setPreviewLoading(true);
    setPreviewError(null);
    setTextContent(null);
    setConvertedFormat(null);
    try {
      const headers = getAuthHeaders();

      // Step 1: Get preview metadata (mime type, format, delivery method)
      const metaResponse = await api.getDocumentPreviewUrl(docId, headers);
      const meta = metaResponse.data;

      setPreviewMimeType(meta.mime_type);
      setConvertedFormat(meta.converted_format || null);
      setPreviewable(meta.previewable !== false);

      if (meta.previewable === false) {
        setPreviewError('This file format is not supported for preview. Please download to view.');
        setPreviewLoading(false);
        return;
      }

      // Always fetch the file bytes THROUGH THE BACKEND for in-browser rendering.
      // The backend downloads from S3 server-side and streams to us, so this works
      // identically in S3 and local modes WITHOUT relying on S3 CORS configuration.
      // (Pre-signed S3 URLs are used for downloads via browser navigation, but
      // pdf.js / fetch-based viewers fail on cross-origin S3 URLs — "Failed to fetch".)
      const fileResponse = await api.getDocumentPreview(docId, headers);

      const isConverted = meta.converted_format === 'html';
      const blobType = isConverted ? 'text/html' : meta.mime_type;
      const blob = new Blob([fileResponse.data], { type: blobType });

      // For text files, extract content for display
      const isTextFile = meta.mime_type?.startsWith('text/') ||
                         meta.mime_type?.includes('json') ||
                         meta.mime_type?.includes('xml');
      if (isTextFile && !isConverted) {
        const text = await blob.text();
        setTextContent(text);
      }

      const blobUrl = URL.createObjectURL(blob);
      setPreviewUrl(blobUrl);
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewError('Preview not available');
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderPreview = () => {
    if (previewLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (previewError || !previewUrl) {
      return (
        <div className="bg-slate-50 rounded-lg p-8 text-center min-h-[400px] flex items-center justify-center">
          <div>
            <FileText className="w-24 h-24 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">{previewError || 'Preview not available'}</p>
            <p className="text-sm text-slate-500 mt-2">Download the document to view contents</p>
          </div>
        </div>
      );
    }

    // Determine preview type based on mime type
    const isPDF = previewMimeType?.includes('pdf');
    const isImage = previewMimeType?.startsWith('image/');
    const isText = previewMimeType?.startsWith('text/') || previewMimeType?.includes('json') || previewMimeType?.includes('xml');

    if (isPDF) {
      return (
        <div className="min-h-[600px] rounded-lg border overflow-hidden">
          <PDFViewer 
            url={previewUrl} 
            onError={(err) => {
              console.error('PDF viewer error:', err);
              setPreviewError('Failed to load PDF');
            }}
          />
        </div>
      );
    }

    if (isImage) {
      return (
        <div className="flex items-center justify-center bg-slate-50 rounded-lg p-4 min-h-[400px]">
          <img
            src={previewUrl}
            alt={document?.title || 'Document preview'}
            className="max-w-full max-h-[600px] object-contain rounded"
          />
        </div>
      );
    }

    // For text files - show the actual content
    if (isText && textContent) {
      return (
        <div className="bg-slate-50 rounded-lg p-4 min-h-[400px] max-h-[600px] overflow-auto">
          <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">
            {textContent}
          </pre>
        </div>
      );
    }

    // For DOCX/XLSX converted to HTML - render in iframe
    if (convertedFormat === 'html' && previewUrl) {
      return (
        <div className="rounded-lg border overflow-hidden min-h-[500px]">
          <iframe
            src={previewUrl}
            title={document?.title || 'Document Preview'}
            className="w-full min-h-[600px] bg-white"
            style={{ border: 'none' }}
            sandbox="allow-same-origin"
          />
        </div>
      );
    }

    // For unsupported formats - show download prompt
    return (
      <div className="bg-slate-50 rounded-lg p-8 text-center min-h-[400px] flex items-center justify-center">
        <div>
          <FileText className="w-24 h-24 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">This file format cannot be previewed directly</p>
          <p className="text-sm text-slate-500 mt-2 mb-4">
            Download the document to view full contents
          </p>
          <Button onClick={handleDownload} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Download to View
          </Button>
        </div>
      </div>
    );
  };

  const handleSaveMetadata = async () => {
    try {
      const headers = getAuthHeaders();
      await api.updateDocument(id, metadata, headers);
      toast.success('Metadata updated successfully');
      setEditingMetadata(false);
      loadDocumentDetails();
    } catch (error) {
      console.error('Error updating metadata:', error);
      toast.error('Failed to update metadata');
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      const headers = getAuthHeaders();
      // Use the pre-signed URL endpoint for direct S3 download.
      // Falls back to backend streaming automatically in local mode.
      const res = await api.getDocumentPresignedUrl(id, headers);
      const { url, filename } = res.data;
      const safeName = filename || document?.title || 'document';

      if (url?.startsWith('http')) {
        // Pre-signed S3 URL — open directly, browser handles the download
        const a = window.document.createElement('a');
        a.href = url;
        a.download = safeName;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
      } else {
        // Local fallback: stream through backend
        const downloadRes = await api.downloadDocument(id, headers);
        const contentType = downloadRes.headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([downloadRes.data], { type: contentType });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = blobUrl;
        a.setAttribute('download', safeName);
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }

      toast.success('Download started successfully');
    } catch (error) {
      console.error('Error downloading:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to download document';
      toast.error(errorMsg);
    }
  };

  const handleFullView = () => {
    setFullViewError(null);
    
    if (!previewUrl) {
      setFullViewError('Document preview not available. Try downloading the file instead.');
      toast.error('Preview not available');
      return;
    }
    
    try {
      // Open the in-app fullscreen overlay (was: window.open in a new tab)
      setFullscreen(true);
    } catch (error) {
      console.error('Error opening full view:', error);
      setFullViewError('Could not open full view. Please download the document instead.');
      toast.error('Could not open full view');
    }
  };

  const handleIndexDocument = async () => {
    try {
      const headers = getAuthHeaders();
      setAiSummary(null);
      setAiInsight(null);
      setIndexing(true);
      toast.info('Starting AI indexing... This may take 15-30 seconds.');
      await api.indexDocument(id, headers);
      
      // Poll for AI results (background processing)
      let attempts = 0;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const docResp = await api.getDocument(id, headers);
          const insight = docResp.data?.__ai_insight || docResp.data?.metadata?.__ai_insight;
          if (insight?.summary && (insight?.status === 'completed' || insight?.status === 'failed')) {
            setAiSummary(insight.summary);
            setAiInsight(insight);
            setDocument(docResp.data);
            setIndexing(false);
            clearInterval(pollInterval);
            if (insight.status === 'completed') {
              toast.success('AI summary generated successfully');
            } else if (insight.status === 'failed') {
              toast.error('AI processing issue: ' + (insight.error || insight.ai_note || 'Please try again'));
            }
          }
        } catch (e) { /* ignore poll errors */ }
        if (attempts >= 20) {
          clearInterval(pollInterval);
          setIndexing(false);
          toast.info('Indexing in progress. Refresh to see results.');
        }
      }, 2000);
    } catch (error) {
      console.error('Error indexing:', error);
      setIndexing(false);
      toast.error(error.response?.data?.detail || 'Failed to index document');
    }
  };

  const [rerunningOcr, setRerunningOcr] = useState(false);
  const handleRerunOcr = async () => {
    setRerunningOcr(true);
    try {
      await api.rerunOcr(id, getAuthHeaders());
      toast.success('OCR completed — document text extracted');
      loadDocumentDetails();
    } catch (error) {
      // 402 quota errors are surfaced by the global QuotaExceededModal.
      if (error.response?.status !== 402) {
        toast.error(error.response?.data?.detail || 'Failed to run OCR');
      }
    } finally {
      setRerunningOcr(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Document not found</h2>
        <Button onClick={() => navigate('/documents')} className="mt-4">
          Back to Documents
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* OCR skipped notice — offer to re-run once the tenant has OCR pages */}
      {document.ocr_skipped && (
        <div data-testid="ocr-skipped-banner"
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900">Text not extracted — OCR was skipped</p>
              <p className="text-sm text-amber-800">This scanned document wasn't OCR'd because your OCR page quota was exhausted at upload. Re-run OCR now that pages are available.</p>
            </div>
          </div>
          <Button onClick={handleRerunOcr} disabled={rerunningOcr}
            data-testid="rerun-ocr-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">
            {rerunningOcr ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running OCR…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Re-run OCR</>}
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/documents')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{document.title}</h1>
            <p className="text-slate-600 mt-1">
              {document.doc_type} • Version {document.current_version}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleDownload} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
          <Button
            onClick={() => navigate(`/documents/${document.id}/annotate`)}
            variant="outline"
            data-testid="annotate-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Annotate
          </Button>
          <Button
            onClick={() => setShareDialogOpen(true)}
            variant="outline"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
          <Button onClick={handleIndexDocument} variant="outline" title="Index for AI Search" disabled={indexing} data-testid="index-for-ai-btn">
            {indexing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Indexing...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Index for AI
              </>
            )}
          </Button>
          {document.is_checked_out && document.checked_out_by === document.created_by && (
            <Button variant="outline">Check In New Version</Button>
          )}
        </div>
      </div>

      {/* Status Badges */}
      <div className="flex gap-2">
        <span className={`px-3 py-1 rounded-full text-sm ${
          document.risk_level === 'High' ? 'bg-red-100 text-red-700' :
          document.risk_level === 'Medium' ? 'bg-orange-100 text-orange-700' :
          'bg-green-100 text-green-700'
        }`}>
          {document.risk_level} Risk
        </span>
        {document.legal_hold && (
          <span className="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700">
            Legal Hold
          </span>
        )}
        {document.is_checked_out && (
          <span className="px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-700">
            Checked Out
          </span>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Preview & AI Summary */}
        <div className="lg:col-span-2 space-y-6">
          {/* Preview Panel */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Document Preview</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleFullView}
                disabled={previewLoading || !previewUrl}
                data-testid="full-view-btn"
              >
                <Maximize2 className="w-4 h-4 mr-2" />
                Full View
              </Button>
            </CardHeader>
            <CardContent>
              {fullViewError && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                  {fullViewError}
                </div>
              )}
              {renderPreview()}
            </CardContent>
          </Card>

          {/* AI Summary Panel - Always show, with appropriate state */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                AI-Generated Summary
                {aiInsight?.detected_language && aiInsight.detected_language !== 'unknown' && (
                  <span className="text-xs font-normal bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">
                    {aiInsight.detected_language.toUpperCase()}
                  </span>
                )}
                {aiInsight?.status === 'completed' && (
                  <CheckCircle className="w-4 h-4 text-green-500 ml-1" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {indexing ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <p className="text-slate-500 text-sm">AI is analyzing document content...</p>
                  <p className="text-slate-400 text-xs mt-1">This may take 15-30 seconds</p>
                </div>
              ) : aiSummary ? (
                <div className="space-y-4">
                  <div className="prose prose-sm max-w-none">
                    <p className="text-slate-700 whitespace-pre-wrap" data-testid="ai-summary-text">{aiSummary}</p>
                  </div>
                  {/* Show extracted fields if available */}
                  {aiInsight?.extracted_fields && Object.keys(aiInsight.extracted_fields).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Extracted Fields</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(aiInsight.extracted_fields).slice(0, 8).map(([key, value]) => (
                          <div key={key} className="text-sm min-w-0">
                            <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>{' '}
                            <span className="text-slate-700 font-medium break-words">
                              {value && typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Show entities if available */}
                  {aiInsight?.entities && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Detected Entities</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(aiInsight.entities)
                          .filter(([_, vals]) => Array.isArray(vals) && vals.length > 0)
                          .flatMap(([type, vals]) => 
                            vals.slice(0, 3).map(v => (
                              <span key={`${type}-${v}`} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded break-words max-w-full">
                                {String(v)}
                              </span>
                            ))
                          )}
                      </div>
                    </div>
                  )}
                  {aiInsight?.status === 'failed' && (
                    <p className="text-xs text-red-600 mt-2" data-testid="ai-error-message">
                      {aiInsight?.error || aiInsight?.ai_note || 'AI processing failed. Please try re-indexing.'}
                    </p>
                  )}
                  {aiInsight?.ai_note && aiInsight?.status !== 'failed' && (
                    <p className="text-xs text-amber-600 mt-2" data-testid="ai-note-message">
                      {aiInsight.ai_note}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-500 text-sm">
                    AI summary not available yet for this document.
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    Click &ldquo;Index for AI&rdquo; to generate a content-based summary.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Numeric Validation (deterministic, math-based) */}
          {numericValidation && numericValidation.rules_evaluated > 0 && (
            <Card data-testid="numeric-validation-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  Numeric Validation
                  <span
                    data-testid="numeric-validation-status"
                    className={`text-xs font-normal px-2 py-0.5 rounded-full ml-2 ${
                      numericValidation.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {numericValidation.passed
                      ? 'All checks passed'
                      : `${numericValidation.violations.length} mismatch${numericValidation.violations.length === 1 ? '' : 'es'}`}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-500">
                  Deterministic arithmetic checks (±{numericValidation.tolerance_pct}% tolerance) — computed independently of the LLM.
                </p>
                {numericValidation.violations.map((v, i) => (
                  <div key={`v-${i}`} data-testid={`nv-violation-${i}`} className="border border-red-200 bg-red-50 rounded-md p-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-red-700">
                      <AlertCircle className="w-4 h-4" />{v.description}
                    </div>
                    <div className="text-xs text-red-600 mt-1">
                      Expected {v.expected} · Computed {v.computed} · Diff {v.difference}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{v.evidence}</div>
                  </div>
                ))}
                {numericValidation.passed_rules.map((v, i) => (
                  <div key={`p-${i}`} data-testid={`nv-passed-${i}`} className="border border-emerald-200 bg-emerald-50 rounded-md p-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                      <CheckCircle className="w-4 h-4" />{v.description}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{v.evidence}</div>
                  </div>
                ))}
                {numericValidation.warnings?.length > 0 && (
                  <p className="text-xs text-amber-600">
                    {numericValidation.warnings.length} rule(s) skipped — required fields not present.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Document Lineage timeline (P3-B) */}
          {lineage && lineage.length > 0 && (
            <Card data-testid="lineage-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-amber-600" />
                  Document Lineage
                  <span className="text-xs font-normal text-slate-400 ml-1">{lineage.length} events</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative pl-2" data-testid="lineage-timeline">
                  {lineage.map((ev, i) => {
                    const cfg = {
                      upload: { icon: Upload, color: '#2563eb', bg: 'bg-blue-50', ring: 'border-blue-200', label: 'Upload' },
                      checkin: { icon: Upload, color: '#2563eb', bg: 'bg-blue-50', ring: 'border-blue-200', label: 'Check-in' },
                      ocr_extraction: { icon: ScanLine, color: '#7c3aed', bg: 'bg-purple-50', ring: 'border-purple-200', label: 'OCR Extraction' },
                      ai_processing: { icon: Brain, color: '#7c3aed', bg: 'bg-purple-50', ring: 'border-purple-200', label: 'AI Processing' },
                      workflow_step: { icon: GitBranch, color: '#d97706', bg: 'bg-amber-50', ring: 'border-amber-200', label: 'Workflow Step' },
                      final_action: { icon: CheckCircle, color: '#059669', bg: 'bg-emerald-50', ring: 'border-emerald-200', label: 'Final Action' },
                      audit: { icon: Activity, color: '#64748b', bg: 'bg-slate-50', ring: 'border-slate-200', label: 'Audit' },
                    }[ev.stage] || { icon: Activity, color: '#64748b', bg: 'bg-slate-50', ring: 'border-slate-200', label: ev.stage };
                    const Icon = cfg.icon;
                    const open = expandedStage === i;
                    const details = Object.entries(ev.details || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
                    return (
                      <div key={i} className="flex gap-3 pb-4 last:pb-0" data-testid={`lineage-event-${i}`}>
                        <div className="flex flex-col items-center">
                          <div className={`p-2 rounded-full border ${cfg.ring} ${cfg.bg}`}>
                            <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                          </div>
                          {i < lineage.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                        </div>
                        <div className="flex-1 min-w-0 pb-1">
                          <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedStage(open ? null : i)}>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{cfg.label}</p>
                              <p className="text-xs text-slate-500">
                                {ev.actor} · {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}
                              </p>
                            </div>
                            {details.length > 0 && (
                              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                          {open && details.length > 0 && (
                            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 bg-slate-50 rounded-md p-3 text-xs" data-testid={`lineage-details-${i}`}>
                              {details.map(([k, v]) => (
                                <div key={k} className="flex justify-between gap-2">
                                  <span className="text-slate-400 capitalize">{k.replace(/_/g, ' ')}</span>
                                  <span className="text-slate-700 font-medium text-right truncate">{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Insights Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                AI Insights & Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AgentInsightsPanel headers={getAuthHeaders()} docId={id} />
            </CardContent>
          </Card>

          {/* Sensitive Data Detection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-600" />
                Sensitive Data Detection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RedactionPanel 
                headers={getAuthHeaders()} 
                docId={id}
                initialText={textContent || ''} 
              />
            </CardContent>
          </Card>

          {/* Document AI Insights (IDP Layer) */}
          <DocumentAIInsights
            documentId={id}
            metadata={{
              ...document?.metadata,
              __ai_insight: document?.__ai_insight || document?.metadata?.__ai_insight,
              __vision_ai: document?.__vision_ai || document?.metadata?.__vision_ai
            }}
            documentType={document?.doc_type}
            headers={getAuthHeaders()}
            onRefresh={loadDocumentDetails}
          />

          {/* Workflow */}
          <Card data-testid="workflow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-amber-600" /> Workflow
                </CardTitle>
                {!hasActiveWorkflow && (
                  <Button
                    size="sm"
                    data-testid="start-workflow-btn"
                    onClick={() => setStartDialogOpen(true)}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <Play className="w-4 h-4 mr-1.5" /> Start Workflow
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!workflowStatus ? (
                <p className="text-sm text-slate-500" data-testid="workflow-empty">
                  No workflow has been started for this document yet. Click <b>Start Workflow</b> to
                  route it through an approval process.
                </p>
              ) : (
                <div className="space-y-4" data-testid="workflow-status">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Workflow:</span>
                    <span className="text-sm text-slate-600">{workflowStatus.workflow_name || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Current Step:</span>
                    <span className="text-sm text-slate-600">{workflowStatus.current_step}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Status:</span>
                    <span data-testid="workflow-status-badge" className={`text-sm px-2 py-1 rounded ${
                      workflowStatus.status === 'Approved' ? 'bg-green-100 text-green-700' :
                      workflowStatus.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                      workflowStatus.status === 'Changes Requested' ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {workflowStatus.status}
                    </span>
                  </div>
                  {workflowStatus.assigned_roles?.length > 0 && hasActiveWorkflow && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Pending With:</span>
                      <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                        <Users className="w-3 h-3" /> {workflowStatus.assigned_roles.join(', ')}
                      </span>
                    </div>
                  )}

                  {/* Approval history timeline */}
                  {workflowStatus.history?.length > 0 && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 mb-2">Approval History</p>
                      <div className="space-y-2" data-testid="workflow-history">
                        {workflowStatus.history.map((h, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <div className="w-2 h-2 rounded-full bg-amber-400 mt-1" />
                            <div className="flex-1">
                              <span className="font-medium text-slate-700">{h.action}</span>
                              <span className="text-slate-400"> · {h.step}</span>
                              {h.comments && <p className="text-slate-500 mt-0.5">{h.comments}</p>}
                              {h.timestamp && (
                                <p className="text-slate-400">{new Date(h.timestamp).toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Inline approve / reject / request-changes actions */}
                  {hasActiveWorkflow && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                      <Button size="sm" disabled={wfActionLoading} data-testid="wf-approve-btn"
                        onClick={() => handleWorkflowAction('approve')}
                        className="bg-green-600 hover:bg-green-700">
                        <CheckCircle className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" disabled={wfActionLoading} data-testid="wf-reject-btn"
                        onClick={() => handleWorkflowAction('reject')}
                        className="text-red-600 border-red-200 hover:bg-red-50">
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                      <Button size="sm" variant="outline" disabled={wfActionLoading} data-testid="wf-request-changes-btn"
                        onClick={() => handleWorkflowAction('request_changes')}
                        className="text-amber-600 border-amber-200 hover:bg-amber-50">
                        <RotateCcw className="w-4 h-4 mr-1" /> Request Changes
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Metadata & Details */}
        <div className="space-y-6">
          {/* Metadata Panel */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Metadata</CardTitle>
                {!editingMetadata ? (
                  <Button size="sm" variant="ghost" onClick={() => setEditingMetadata(true)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={handleSaveMetadata}>
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingMetadata(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingMetadata ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Title</label>
                    <Input
                      value={metadata.title}
                      onChange={(e) => setMetadata({...metadata, title: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Input
                      value={metadata.description || ''}
                      onChange={(e) => setMetadata({...metadata, description: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Document Type</label>
                    <Input
                      value={metadata.doc_type}
                      onChange={(e) => setMetadata({...metadata, doc_type: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
                    <Input
                      value={metadata.tags?.join(', ') || ''}
                      onChange={(e) => setMetadata({...metadata, tags: e.target.value.split(',').map(t => t.trim())})}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm font-medium text-slate-700">Description</p>
                    <p className="text-sm text-slate-600 mt-1">{document.description || 'No description'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">Document Type</p>
                    <p className="text-sm text-slate-600 mt-1">{document.doc_type}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">Risk Score</p>
                    <p className="text-sm text-slate-600 mt-1">{document.risk_score.toFixed(1)}/100</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">Tags</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {document.tags?.length > 0 ? (
                        document.tags.map((tag, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded">
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500">No tags</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">Created</p>
                    <p className="text-sm text-slate-600 mt-1">
                      {new Date(document.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">Last Updated</p>
                    <p className="text-sm text-slate-600 mt-1">
                      {new Date(document.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Version List */}
          <Card>
            <CardHeader>
              <CardTitle>Version History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {versions.length > 0 ? (
                  versions.map((version) => (
                    <div key={version.id} className="flex items-start justify-between p-3 bg-slate-50 rounded">
                      <div>
                        <p className="text-sm font-medium">Version {version.version_number}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(version.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost">
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-500">No version history available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {auditLogs.length > 0 ? (
                  auditLogs.slice(0, 5).map((log) => (
                    <div key={log.id} className="flex items-start gap-3 p-2 border-l-2 border-blue-200">
                      <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{log.action}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-500">No activity logs available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Share dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        docId={document?.id}
        docTitle={document?.title}
      />

      {/* Start Workflow dialog */}
      <Dialog open={startDialogOpen} onOpenChange={setStartDialogOpen}>
        <DialogContent data-testid="start-workflow-dialog">
          <DialogHeader>
            <DialogTitle>Start a Workflow</DialogTitle>
            <DialogDescription>
              Select an approval workflow to route this document through. The first step will be
              assigned to the configured approver(s) and appear in their My Tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {workflows.length === 0 ? (
              <p className="text-sm text-slate-500">
                No active workflows are available. Create one under Workflows → Definitions first.
              </p>
            ) : (
              <Select value={selectedWorkflowId} onValueChange={setSelectedWorkflowId}>
                <SelectTrigger data-testid="workflow-select">
                  <SelectValue placeholder="Choose a workflow" />
                </SelectTrigger>
                <SelectContent>
                  {workflows.map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`workflow-option-${w.id}`}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleStartWorkflow}
              disabled={startingWf || !selectedWorkflowId}
              data-testid="confirm-start-workflow-btn"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {startingWf ? 'Starting…' : 'Start Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fullscreen viewer overlay */}
      {fullscreen && previewUrl && (
        <div
          data-testid="fullscreen-viewer"
          className="fixed top-0 left-0 w-screen bg-white"
          style={{ height: '100vh', zIndex: 9999 }}
        >
          {/* Top toolbar */}
          <div
            className="h-12 border-b border-slate-200 px-4 flex items-center gap-2 bg-white shrink-0"
            data-testid="fullscreen-toolbar"
          >
            <FileText className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-900 truncate flex-1" title={document?.title}>
              {document?.title}
            </span>

            <Button size="sm" variant="ghost" onClick={() => setFsZoom((z) => Math.max(z - 0.25, 0.5))} data-testid="fs-zoom-out" title="Zoom out (-)">
              <span className="text-base font-bold">−</span>
            </Button>
            <span className="text-xs text-slate-600 w-12 text-center">{Math.round(fsZoom * 100)}%</span>
            <Button size="sm" variant="ghost" onClick={() => setFsZoom((z) => Math.min(z + 0.25, 3))} data-testid="fs-zoom-in" title="Zoom in (+)">
              <span className="text-base font-bold">+</span>
            </Button>

            {(previewMimeType?.includes('pdf')) && (
              <>
                <div className="w-px h-5 bg-slate-200 mx-1" />
                <Button size="sm" variant="ghost" onClick={() => window.dispatchEvent(new CustomEvent('pdfviewer:nav', { detail: { direction: 'prev' } }))} data-testid="fs-prev-page" title="Previous page (←)">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-slate-600 w-20 text-center" data-testid="fs-page-indicator">
                  Page {fsPage.current} of {fsPage.total || '—'}
                </span>
                <Button size="sm" variant="ghost" onClick={() => window.dispatchEvent(new CustomEvent('pdfviewer:nav', { detail: { direction: 'next' } }))} data-testid="fs-next-page" title="Next page (→)">
                  <Edit className="w-4 h-4 rotate-180" style={{ transform: 'scaleX(-1)' }} />
                </Button>
              </>
            )}

            <div className="w-px h-5 bg-slate-200 mx-1" />
            <Button size="sm" variant="ghost" onClick={handleDownload} data-testid="fs-download" title="Download">
              <Download className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => window.print()} data-testid="fs-print" title="Print">
              <span className="text-xs">Print</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setFullscreen(false)} data-testid="fs-close" title="Close (Esc)">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Viewer area */}
          <div className="bg-slate-100 overflow-auto" style={{ height: 'calc(100vh - 48px)' }}>
            {previewMimeType?.includes('pdf') ? (
              <PDFViewer
                url={previewUrl}
                zoom={fsZoom}
                hideToolbar={true}
                onPageChange={(c, t) => setFsPage({ current: c, total: t || 0 })}
                onError={(err) => console.error('PDF preview error:', err)}
              />
            ) : previewMimeType?.startsWith('image/') ? (
              <div className="flex justify-center p-8">
                <img
                  src={previewUrl}
                  alt={document?.title}
                  style={{ transform: `scale(${fsZoom})`, transformOrigin: 'top center' }}
                  className="max-w-full shadow-lg"
                />
              </div>
            ) : (
              <iframe
                src={previewUrl}
                title={document?.title}
                className="w-full h-full"
                style={{ border: 0 }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDetail;
